import { useCallback, useEffect, useRef, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";

interface ProgramParticipantsControllerProps {
  query: string;
  onAddParticipants: () => void;
  totalRecords: number;
  isLead: boolean;
  isProgramClosed: boolean;
  onSearch: (value: string) => void;
}

export function ProgramParticipantsController({
  query,
  onAddParticipants,
  totalRecords = 0,
  isLead = false,
  isProgramClosed = false,
  onSearch,
}: ProgramParticipantsControllerProps) {
  const [localQuery, setLocalQuery] = useState(query);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update local state when external query changes
  useEffect(() => {
    setLocalQuery(query);
  }, [query]);

  const debouncedSearch = useCallback(
    (value: string) => {
      setLocalQuery(value);

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout
      timeoutRef.current = setTimeout(() => {
        onSearch(value);
      }, 400);
    },
    [onSearch],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="w-full flex flex-col md:flex-row justify-between gap-4">
      <div className="flex items-center gap-2">
        <div className="text-lg font-semibold">Participants</div>
        <Badge className="text-sm bg-gray-200 text-gray-500">
          {totalRecords}
        </Badge>
      </div>

      <div className="flex justify-between gap-2">
        <div className="w-full md:w-60">
          <SearchInput
            value={localQuery}
            onChange={debouncedSearch}
            loading={false}
            placeholder="Search Participants..."
          />
        </div>

        {isLead && !isProgramClosed && (
          <Button onClick={onAddParticipants} intent="primary" size="md">
            + Add
          </Button>
        )}
      </div>
    </div>
  );
}
